import React, { useMemo, useState } from "react";
import { Table, Upload, Button, message, Tag, Row, Col, Empty, TablePaginationConfig } from "antd";
import { ExportOutlined, UploadOutlined } from "@ant-design/icons";
import { exportProfanityResultToCSV, processProfanityFile } from "./utils";
import { ComponentTitle, PageTitleLastCol } from "../custom-components";
import styled from "styled-components";
import UploadModal from "./UploadModal";
import useScreenSizeHook from "../utils/useScreenSizeHook";
import { CustomTable } from "../series/StyledComponent";


const PageContent = styled.div`
    background: white;
    width: 100%;
    min-height: calc(100vh - 130px);
    border-radius: 8px;
    & .ant-empty {
      padding: 24px;
      & .empty-text {
        margin-bottom: 16px;
      }
    }
`;


const columns = [
  {
    title: "Timecode",
    dataIndex: "timecode",
    key: "timecode",
    width: 180,
  },
  {
    title: "Severity",
    dataIndex: "severity",
    key: "severity",
    width: 160,
    render: (sev: string, record: any) =>
      (() => {
        let severityColor = "blue";
        let severityLabel = sev?.charAt(0).toUpperCase() + sev?.slice(1);

        if (sev === "high") {
          severityColor = "red";
          severityLabel = "High";
        } else if (sev === "medium") {
          severityColor = "orange";
          severityLabel = "Medium";
        }

        const bleepCategory = record?.bleep_category
          ? record.bleep_category.charAt(0).toUpperCase() + record.bleep_category.slice(1)
          : undefined;

        return (
          <>
            <Tag color={severityColor}>{severityLabel}</Tag>
            {(record.bleep || bleepCategory) && <Tag color="red">{record.ai_rechecked && "AI: "}{bleepCategory ?? "Bleep"}</Tag>}
          </>
        );
      })()
  },
  {
    title: "Term",
    dataIndex: "term",
    key: "term",
    width: 120,
    render: (term: string) => <Tag color="purple">{term}</Tag>,
  },
  {
    title: "Text Snippet",
    dataIndex: "textSnippet",
    key: "textSnippet",
    width: 400,
    render: (textSnippet: string) => (
      <span style={{ fontStyle: "italic" }}>{textSnippet}</span>
    ),
  },
];

const ProfanityCheck: React.FC = () => {
  const { height, } = useScreenSizeHook();
  const [fileList, setFileList] = useState<any[]>([]);
  const [inputFileName, setInputFileName] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [reprocessForBleeps, setReprocessForBleeps] = useState(true);
  const [results, setResults] = useState<any[]>([]);
  const [flaggedResults, setFlaggedResults] = useState<any[]>([]);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const handleBeforeUpload = (file: File) => {
    const fileType = file.name.split(".").pop()?.toLowerCase();
    if (!["vtt", "scc", "srt"].includes(fileType || "")) {
      message.error("You can only upload .srt, .vtt, or .scc files!");
      return Upload.LIST_IGNORE;
    }
    setFileList([file]);
    setInputFileName(file.name);
    return false;
  };

  const handleUpload = async () => {
    if (!fileList.length) {
      message.warning("Please select a file to upload.");
      return;
    }
    setProcessing(true);
    setResults([]);
    try {
      const {flaggedData, allData} = await processProfanityFile(fileList[0], reprocessForBleeps);
      setResults(allData);
      setFlaggedResults(flaggedData);
      message.success("Processing complete!");
      setUploadModalOpen(false);
    } catch (err) {
      message.error("Failed to process file.");
    } finally {
      setProcessing(false);
    }
  };

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setCurrentPage(pagination.current || 1);
    setPageSize(pagination.pageSize || 10);
  };

  const uploadButton = useMemo(() => {
      return (
        <Button 
              type="primary" 
              icon={<UploadOutlined />}
              onClick={() => setUploadModalOpen(true)}
          >
              Upload Caption File
        </Button>
      )
  }, [])

  return (
    <>
        <Row justify="space-between" align="middle">
            <Col>
                <ComponentTitle>Profanity Check</ComponentTitle>
            </Col>
            <PageTitleLastCol>
              {uploadButton}
               &nbsp;
                <Button 
                    type="primary" 
                    disabled={!results.length}
                    onClick={() => {
                        if (results.length && inputFileName) {
                            exportProfanityResultToCSV(inputFileName, results);
                        } else {
                            message.error("No data found.");
                        }
                    }}
                    icon={<ExportOutlined />}
                >
                    Export All
                </Button> &nbsp;
                <Button 
                    type="primary" 
                    disabled={!flaggedResults.length}
                    onClick={() => {
                        if (flaggedResults.length && inputFileName) {
                            exportProfanityResultToCSV(inputFileName, flaggedResults);
                        } else {
                            message.error("No data found.");
                        }
                    }}
                    icon={<ExportOutlined />}
                >
                    Export Flagged
                </Button>
            </PageTitleLastCol>
        </Row>
        <PageContent>
            <UploadModal
                open={uploadModalOpen}
                onCancel={() => setUploadModalOpen(false)}
                fileList={fileList}
                setFileList={setFileList}
                processing={processing}
                reprocessForBleeps={reprocessForBleeps}
                setReprocessForBleeps={setReprocessForBleeps}
                handleBeforeUpload={handleBeforeUpload}
                handleUpload={async () => {
                    await handleUpload();
                }}
                setResults={setResults}
                setInputFileName={setInputFileName}
            />
            {results.length > 0 ? (
                <>
                    <CustomTable
                        columns={columns}
                        dataSource={flaggedResults}
                        pagination={{
                            current: currentPage,
                            pageSize: pageSize,
                            total: flaggedResults.length,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            pageSizeOptions: [10, 20, 50, 100],
                        }}
                        // scroll={{y: height - 64}}
                        bordered
                        rowKey="key"
                        onChange={handleTableChange}
                        scroll={{ x: true }}
                        sticky={{ offsetHeader: 0, offsetScroll: 0 }} // fix header
                    />
                </>
            ) : (
                <Empty>
                    <Row>
                      <Col className="empty-text" span={24}>Please uplaod a file to see the data.</Col>
                      <Col span={24}>
                        {uploadButton}
                      </Col>
                    </Row>
                </Empty>
            )}
      </PageContent>
    </>
  );
};

export default ProfanityCheck;